import React, { useState, useEffect, useMemo } from "react";
import { Plus, Edit2, Trash2, DollarSign } from "lucide-react";
import { Pagination } from "../components/common/Pagination";
import { SearchInput } from "../components/ui/SearchInput";
import { commissionTypesService } from "../api/services/commissionTypes/commissionTypesService";
import { customerService } from "../api/services/customers/customerService";
import { agentsService } from "../api/services/agents/agentsService";
import {
  Commission,
  CommissionType,
  CreateCommissionRequest,
} from "../api/services/commissionTypes/types";
import { Customer } from "../types/customer";
import { Agent } from "../api/services/agents/type";
import PrimaryButton from "../components/ui/PrimaryButton";
import toast from "react-hot-toast";
import { ConfirmDialog } from "../components/ui/ConfirmDialog";

export const PlayerCommissions: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const [commissions, setCommissions] = useState<Commission[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingCommission, setEditingCommission] = useState<Commission | null>(
    null,
  );
  const [deleteCommission, setDeleteCommission] = useState<Commission | null>(
    null,
  );

  // Form data
  const [formData, setFormData] = useState<CreateCommissionRequest>({
    commissionTypeId: "",
    playerId: "",
    agentId: "",
    commissionValue: 0,
    notes: "",
  });
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Reference data
  const [commissionTypes, setCommissionTypes] = useState<CommissionType[]>([]);
  const [players, setPlayers] = useState<Customer[]>([]);
  const [agents, setAgents] = useState<Agent[]>([]);

  const itemsPerPage = 10;

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch data
  useEffect(() => {
    fetchCommissions();
    fetchReferenceData();
  }, [currentPage, debouncedSearchTerm]);

  const fetchCommissions = async () => {
    setLoading(true);
    try {
      const response = await commissionTypesService.getCommissions({
        skip: (currentPage - 1) * itemsPerPage,
        limit: itemsPerPage,
      });
      setCommissions(response.data.items || []);
      setTotalCount(response.data.pagination.total || 0);
    } catch (error) {
      console.error("Error fetching commissions:", error);
      toast.error("Failed to load commissions");
    } finally {
      setLoading(false);
    }
  };

  const fetchReferenceData = async () => {
    try {
      const [typesRes, playersRes, agentsRes] = await Promise.all([
        commissionTypesService.getCommissionTypes({ limit: 100 }),
        customerService.getCustomers({ limit: 100 }),
        agentsService.getAgents({ limit: 100 }),
      ]);
      setCommissionTypes(typesRes.data.items || []);
      setPlayers(playersRes.items || []);
      setAgents(agentsRes.items || []);
    } catch (error) {
      console.error("Error fetching reference data:", error);
    }
  };

  const totalPages = useMemo(() => {
    return Math.ceil(totalCount / itemsPerPage) || 1;
  }, [totalCount]);

  const validateForm = () => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.commissionTypeId) {
      newErrors.commissionTypeId = "Commission type is required";
    }
    if (!formData.playerId) {
      newErrors.playerId = "Player is required";
    }
    if (formData.commissionValue <= 0) {
      newErrors.commissionValue = "Commission value must be greater than 0";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateOrUpdate = async () => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      if (editingCommission) {
        await commissionTypesService.updateCommission(editingCommission.id, {
          commissionValue: formData.commissionValue,
          notes: formData.notes || undefined,
        });
        toast.success("Commission updated successfully");
      } else {
        await commissionTypesService.createCommission(formData);
        toast.success("Commission created successfully");
      }
      setShowModal(false);
      setEditingCommission(null);
      resetForm();
      fetchCommissions();
    } catch (error) {
      console.error("Error saving commission:", error);
      toast.error(
        editingCommission
          ? "Failed to update commission"
          : "Failed to create commission",
      );
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteCommission) return;

    setLoading(true);
    try {
      await commissionTypesService.deleteCommission(deleteCommission.id);
      toast.success("Commission deleted successfully");
      setDeleteCommission(null);
      fetchCommissions();
    } catch (error) {
      console.error("Error deleting commission:", error);
      toast.error("Failed to delete commission");
    } finally {
      setLoading(false);
    }
  };

  const openEditModal = (commission: Commission) => {
    setEditingCommission(commission);
    setFormData({
      commissionTypeId: commission.commissionTypeId,
      playerId: commission.playerId,
      agentId: commission.agentId || "",
      commissionValue: commission.commissionValue,
      notes: commission.notes || "",
    });
    setShowModal(true);
  };

  const openCreateModal = () => {
    setEditingCommission(null);
    resetForm();
    setShowModal(true);
  };

  const resetForm = () => {
    setFormData({
      commissionTypeId: "",
      playerId: "",
      agentId: "",
      commissionValue: 0,
      notes: "",
    });
    setErrors({});
  };

  const formatValue = (value: number) => {
    return new Intl.NumberFormat("en-IN", {
      style: "currency",
      currency: "INR",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-slate-900 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-white">Player Commissions</h1>
          <p className="text-slate-400 mt-1">
            Manage commission assignments for players and agents
          </p>
        </div>

        {/* Search and Actions */}
        <div className="bg-slate-800 rounded-lg p-4 mb-6">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="flex-1">
              <SearchInput
                value={searchTerm}
                onChange={setSearchTerm}
                placeholder="Search by player or agent..."
              />
            </div>
            <PrimaryButton onClick={openCreateModal}>
              <Plus className="w-4 h-4 mr-2" />
              Assign Commission
            </PrimaryButton>
          </div>
        </div>

        {/* Commissions Table */}
        <div className="bg-slate-800 rounded-lg overflow-hidden">
          <table className="w-full">
            <thead className="bg-slate-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Player
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Commission Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Value
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Agent
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Notes
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-slate-300 uppercase tracking-wider">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-700">
              {loading ? (
                <tr>
                  <td colSpan={6} className="px-6 py-12 text-center">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-blue-500"></div>
                    </div>
                  </td>
                </tr>
              ) : commissions.length === 0 ? (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-12 text-center text-slate-400"
                  >
                    No commissions assigned yet
                  </td>
                </tr>
              ) : (
                commissions.map((commission) => (
                  <tr
                    key={commission.id}
                    className="hover:bg-slate-700/50 transition-colors"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-white font-medium">
                        {commission.player?.name || "Unknown Player"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <DollarSign className="w-4 h-4 text-blue-400 mr-2" />
                        <span className="text-slate-300">
                          {commission.commissionType?.name || "Unknown Type"}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-green-400 font-medium">
                        {formatValue(commission.commissionValue)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="text-slate-400 text-sm">
                        {commission.agent?.name || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-slate-400 text-sm">
                        {commission.notes || "-"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <button
                        onClick={() => openEditModal(commission)}
                        className="text-blue-400 hover:text-blue-300 mr-3"
                        title="Edit"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setDeleteCommission(commission)}
                        className="text-red-400 hover:text-red-300"
                        title="Delete"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-6">
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={setCurrentPage}
            />
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center p-4 z-50">
          <div className="bg-slate-800 rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-slate-700">
              <h2 className="text-lg font-semibold text-white">
                {editingCommission ? "Edit Commission" : "Assign Commission"}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Commission Type *
                  </label>
                  <select
                    value={formData.commissionTypeId}
                    onChange={(e) => {
                      const typeId = e.target.value;
                      setFormData({ ...formData, commissionTypeId: typeId });
                      if (errors.commissionTypeId) {
                        const newErrors = { ...errors };
                        delete newErrors.commissionTypeId;
                        setErrors(newErrors);
                      }
                    }}
                    disabled={!!editingCommission}
                    className={`w-full p-3 bg-slate-700 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                      errors.commissionTypeId
                        ? "border-red-500"
                        : "border-slate-600"
                    } ${editingCommission ? "opacity-50" : ""}`}
                  >
                    <option value="">Select commission type...</option>
                    {commissionTypes.map((type) => (
                      <option key={type.id} value={type.id}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                  {errors.commissionTypeId && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.commissionTypeId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Player *
                  </label>
                  <select
                    value={formData.playerId}
                    onChange={(e) => {
                      setFormData({ ...formData, playerId: e.target.value });
                      if (errors.playerId) {
                        const newErrors = { ...errors };
                        delete newErrors.playerId;
                        setErrors(newErrors);
                      }
                    }}
                    disabled={!!editingCommission}
                    className={`w-full p-3 bg-slate-700 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                      errors.playerId ? "border-red-500" : "border-slate-600"
                    } ${editingCommission ? "opacity-50" : ""}`}
                  >
                    <option value="">Select player...</option>
                    {players.map((player) => (
                      <option key={player.id} value={player.id}>
                        {player.name} ({player.playerId})
                      </option>
                    ))}
                  </select>
                  {errors.playerId && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.playerId}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Agent (Optional)
                  </label>
                  <select
                    value={formData.agentId}
                    onChange={(e) =>
                      setFormData({ ...formData, agentId: e.target.value })
                    }
                    className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="">No agent...</option>
                    {agents.map((agent) => (
                      <option key={agent.id} value={agent.id}>
                        {agent.name} ({agent.agentId})
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-300 mb-2">
                    Commission Value (₹) *
                  </label>
                  <input
                    type="number"
                    step="1"
                    value={formData.commissionValue}
                    onChange={(e) => {
                      setFormData({
                        ...formData,
                        commissionValue: Number(e.target.value),
                      });
                      if (errors.commissionValue) {
                        const newErrors = { ...errors };
                        delete newErrors.commissionValue;
                        setErrors(newErrors);
                      }
                    }}
                    className={`w-full p-3 bg-slate-700 border rounded-lg text-white focus:ring-2 focus:ring-blue-500 ${
                      errors.commissionValue
                        ? "border-red-500"
                        : "border-slate-600"
                    }`}
                    placeholder="10000"
                  />
                  {errors.commissionValue && (
                    <p className="text-red-400 text-sm mt-1">
                      {errors.commissionValue}
                    </p>
                  )}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-slate-300 mb-2">
                  Notes (Optional)
                </label>
                <textarea
                  value={formData.notes}
                  onChange={(e) =>
                    setFormData({ ...formData, notes: e.target.value })
                  }
                  rows={3}
                  className="w-full p-3 bg-slate-700 border border-slate-600 rounded-lg text-white focus:ring-2 focus:ring-blue-500 resize-none"
                  placeholder="Add any notes about this commission..."
                />
              </div>
            </div>

            <div className="flex gap-3 p-6 border-t border-slate-700">
              <button
                onClick={() => {
                  setShowModal(false);
                  setEditingCommission(null);
                  resetForm();
                }}
                className="flex-1 px-4 py-2 border border-slate-600 text-slate-300 hover:bg-slate-700 rounded-lg transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleCreateOrUpdate}
                disabled={loading}
                className="flex-1 bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg transition-colors disabled:opacity-50"
              >
                {loading
                  ? "Saving..."
                  : editingCommission
                    ? "Update"
                    : "Assign Commission"}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation */}
      {deleteCommission && (
        <ConfirmDialog
          open={true}
          onConfirm={handleDelete}
          onCancel={() => setDeleteCommission(null)}
          title="Delete Commission"
          description={`Are you sure you want to delete this commission assignment? This action cannot be undone.`}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}
    </div>
  );
};
